import { HttpStatusCodes } from 'utils/http-status-codes';
import { convertFlowTypeToLink, showError } from './skyla/helpers';
import { HOME } from 'utils/links';
import { getErrorMsg } from './skyla/mappings';
// A small function to help us deal with errors coming from fetching a flow.
export const handleGetFlowError = (navigate, flowType, resetFlow, t) => {
    return async (err) => {
        const error = err.response?.data.error;
        const errorId = error?.id ?? '';
        /* eslint-disable line-comment-position, no-inline-comments -- Case with pass through */
        switch (errorId) {
            case 'session_already_available': {
                /*
                 * User is already signed in. Check if there's a return_to URL
                 * (e.g., deep-link from WebUntis) and redirect there instead of HOME.
                 */
                const params = new URLSearchParams(window.location.search);
                const returnToUrl = params.get('return_to');
                if (returnToUrl) {
                    if (returnToUrl.startsWith(window.location.origin)) {
                        /*
                         * Absolute URL on same origin — strip origin and navigate.
                         * Fall back to '/' when return_to equals the bare origin (no path).
                         */
                        navigate(returnToUrl.replace(window.location.origin, '') || '/', {
                            replace: true,
                        });
                    }
                    else if (returnToUrl.startsWith('/')) {
                        // Relative path — navigate directly
                        navigate(returnToUrl, { replace: true });
                    }
                    else {
                        // External URL — full page redirect
                        window.location.href = returnToUrl;
                    }
                }
                else {
                    navigate(HOME);
                }
                return Promise.resolve();
            }
            case 'browser_location_change_required': // Ory Kratos asked us to point the user to this URL.
            case 'session_aal2_required': // 2FA is enabled and enforced, but user did not perform 2fa yet!
            case 'session_refresh_required': // We need to re-authenticate to perform this action
                if (err.response?.data.redirect_browser_to) {
                    window.location.assign(err.response.data.redirect_browser_to);
                }
                else {
                    showError(t('body.flow.error.missingRedirectParam'));
                }
                return Promise.resolve();
            case 'self_service_flow_return_to_forbidden': // The flow expired, let's request a new one.
            case 'self_service_flow_expired': // The flow expired, let's request a new one.
            case 'security_csrf_violation': // A CSRF violation occurred. Best to just refresh the flow!
            case 'security_identity_mismatch': // The requested item was intended for someone else. Let's request a new flow...
                showError(getErrorMsg(errorId, t));
                resetFlow(undefined);
                navigate(convertFlowTypeToLink(flowType));
                return Promise.resolve();
            default: {
                switch (err.response?.status) {
                    case HttpStatusCodes.UNAUTHORIZED:
                    case HttpStatusCodes.GONE:
                        // The flow expired, let's request a new one.
                        resetFlow(undefined);
                        navigate(convertFlowTypeToLink(flowType));
                        return Promise.resolve();
                    default:
                        // We are not able to handle the error? Return it.
                        return Promise.reject(err);
                }
            }
            /* eslint-enable line-comment-position, no-inline-comments */
        }
    };
};
// A small function to help us deal with errors coming from initializing a flow.
export const handleFlowError = handleGetFlowError;
/**
 * Displays an error message if previous error handler don't handle an error.
 * @param t Translation function with error IDs.
 * @returns Rejected Promise with the error.
 */
export const handleErrorFallback = (t) => {
    return async (err) => {
        const error = err.response?.data.error;
        showError(getErrorMsg(error?.id ?? '', t), error);
        return Promise.reject(err);
    };
};
